import { useState } from 'react';
import {
  centavos,
  costOf,
  formatMoney,
  formatQuantity,
  parseMoney,
  parseQuantity,
  unitCostFromTotal,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import { existenciaId, type Existencia, type Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { enRango } from '../lib/costos.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import { etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo, Selector } from './Campo.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * What one unit of the product costs in one place, set right by the owner (2026-10-13): goods
 * counted with no cost, a cost mixed with a wrong purchase's. «C$ 1,250 por 100» is how a sack's
 * price is known, so it asks for what they cost and for how many. Before saving it says what the
 * stock is worth now and after, and where the difference goes (`inventario.costo_corregido`).
 *
 * Opened from a product's page, and from each row of «Precios/Costos» (2026-10-18), where the
 * owner goes through the products with no cost one after another: there the reason is already
 * written for a product that had none, the cost box has the keyboard, and Enter saves.
 */
export function CorregirCostoInventario({
  producto,
  existencias,
  motivoInicial,
  onCorregido,
  onCerrar,
}: {
  producto: Producto;
  /** The product's own, every place it is held. */
  existencias: readonly Existencia[];
  /** The reason already written, when the screen knows it: a cost set a moment ago, being put right. */
  motivoInicial?: string;
  /** Saved: the screen that opened it, keeping the row it came from in sight. */
  onCorregido?: () => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const lugares = existencias.filter((e) => e.ubicacion === 'tienda' || e.cantidad !== 0);
  const [ubicacion, setUbicacion] = useState(lugares[0]?.ubicacion ?? 'tienda');
  const [costo, setCosto] = useState('');
  const [cuantas, setCuantas] = useState('1');
  /** What it had no cost is the reason itself; a wrong cost's reason is the owner's to say. */
  const [motivo, setMotivo] = useState(
    () => motivoInicial ?? ((existencias.find((e) => e.ubicacion === ubicacion)?.costoPromedio ?? 0) === 0 ? t('costoInv.motivoSinCosto') : ''),
  );
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unidad = etiquetaUnidad(producto.unidadBase, producto.unidadBase);
  const actual = existencias.find((e) => e.ubicacion === ubicacion);
  const cantidad = actual?.cantidad ?? (0 as Milli);
  const antes = actual?.costoPromedio ?? (0 as UnitCost);
  const total = parseMoney(costo);
  const por = parseQuantity(cuantas);
  const nuevo = total !== null && total >= 0 && por !== null && por > 0 ? enRango(() => unitCostFromTotal(total, por)) : null;
  const mismo = nuevo !== null && nuevo === antes;
  const valorAntes = costOf(antes, cantidad);
  const valorDespues = nuevo === null ? null : enRango(() => costOf(nuevo, cantidad));
  const listo = nuevo !== null && !mismo && valorDespues !== null && motivo.trim() !== '' && !ocupado;
  const cada = (c: UnitCost) => formatMoney(costOf(c, 1000 as Milli));
  const alEnter = (e: React.KeyboardEvent) => e.key === 'Enter' && void corregir();

  async function corregir() {
    if (!listo || nuevo === null) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('inventario.costo_corregido', {
        productoId: producto.id,
        ubicacion,
        costoUnitario: nuevo,
        motivo: motivo.trim(),
      });
      const despues = await db.existencias.get(existenciaId(producto.id, ubicacion));
      if (rechazo || despues?.costoPromedio !== nuevo) {
        setOcupado(false);
        setError(rechazo ?? t('costoInv.noSePudo'));
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('costoInv.noSePudo'));
      return;
    }
    onCorregido?.();
    onCerrar();
  }

  return (
    <Modal titulo={t('costoInv.titulo', { producto: nombre(producto) })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5" data-costo-inventario-hoja>
        <p className="text-sm text-slate-400">{t('costoInv.explicacion', { unidad })}</p>
        {lugares.length > 1 && (
          <Selector etiqueta={t('costoInv.lugar')} value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
            {lugares.map((e) => (
              <option key={e.ubicacion} value={e.ubicacion}>
                {etiquetaLugar(e.ubicacion)}
              </option>
            ))}
          </Selector>
        )}
        <p className="tabular text-sm" data-costo-inventario-ahora>
          {t('costoInv.ahora', { costo: cada(antes), unidad, cantidad: formatQuantity(cantidad), valor: formatMoney(valorAntes) })}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Campo
            etiqueta={t('costoInv.costo')}
            inputMode="decimal"
            className="tabular"
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            onKeyDown={alEnter}
            error={mismo ? t('costoInv.mismo') : undefined}
            autoComplete="off"
            autoFocus
            data-costo-inventario-costo
          />
          <Campo
            etiqueta={t('costoInv.cuantas', { unidad })}
            inputMode="decimal"
            className="tabular"
            value={cuantas}
            onChange={(e) => setCuantas(e.target.value)}
            onKeyDown={alEnter}
            autoComplete="off"
            data-costo-inventario-cuantas
          />
        </div>
        <p className="-mt-3 text-xs text-slate-500">{t('costoInv.ayuda')}</p>
        {nuevo !== null && !mismo && valorDespues !== null && (
          <div className="flex flex-col gap-1 rounded-lg bg-slate-800/60 px-3 py-2 text-sm" data-costo-inventario-efecto>
            <p className="tabular">{t('cantidad.cadaUnidad', { unidad, antes: cada(antes), despues: cada(nuevo) })}</p>
            <p className="tabular text-xs text-slate-400">
              {cantidad === 0
                ? t('costoInv.sinExistencia')
                : t('costoInv.valor', {
                    antes: formatMoney(valorAntes),
                    despues: formatMoney(valorDespues),
                    diferencia: formatMoney(centavos(valorDespues - valorAntes), { signed: true }),
                  })}
            </p>
          </div>
        )}
        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={alEnter}
          autoComplete="off"
          data-costo-inventario-motivo
        />
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <button type="button" className="btn-primario" disabled={!listo} onClick={() => void corregir()}>
          <Ico icono="guardar" />
          {t('costoInv.guardar')}
        </button>
      </div>
    </Modal>
  );
}
